import React, { useState } from "react";
import { Avatar as AntAvatar, Tooltip } from "antd";
import { formatReadable as formatName } from "../../utils/formattingUtils";
import { CompassParagraph } from "../common/styledComponents";
import {
  graphPaletteV2,
  greyText,
  lightBlueBackground,
  lightBlueText,
} from "../../styles";
import { ElementContainer } from "../containers/styledComponents";
import styled from "styled-components";
import gaspAvatar from "../../assets/images/gaspard_avatar.png";
import aiLogo from "../../assets/svg/ai_logo.svg";
import { isSystemUser, SYSTEM_USER_DISPLAY_NAME } from "../../constants";
import { pulseDarker } from "../tables/LoadingTableCell";

const StackWrapper = styled.div`
  display: flex;
`;

const LoadingAvatar = styled(AntAvatar)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #dfe5e7;
  animation: ${pulseDarker} 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
`;

type AntAvatarSize = "xxsmall" | "xsmall" | "small" | "default" | "large";

const avatarSizeMap: Record<
  AntAvatarSize,
  { width: number; height: number; fontSize: number; lineHeight: string }
> = {
  xxsmall: { width: 12, height: 12, fontSize: 7, lineHeight: "12px" },
  xsmall: { width: 18, height: 18, fontSize: 9, lineHeight: "18px" },
  small: { width: 24, height: 24, fontSize: 12, lineHeight: "24px" },
  default: { width: 32, height: 32, fontSize: 14, lineHeight: "32px" },
  large: { width: 40, height: 40, fontSize: 16, lineHeight: "40px" },
};

const colors = graphPaletteV2.slice(0, 10);

const getBackground = (username: string): string => {
  if (!username) return "#ccc";

  const firstLetter = username[0].toUpperCase();
  const letterPosition = firstLetter.charCodeAt(0) - 65 + 1;
  const index = letterPosition % colors.length;

  return colors[index];
};

const KNOWN_USER_AVATARS: { test: (u: string) => boolean; src: string }[] = [
  {
    test: (u) => isSystemUser(u) || /compass[\s_-]?atlas/i.test(u),
    src: aiLogo,
  },
  { test: (u) => /gaspard/i.test(u), src: gaspAvatar },
];

const getDefaultAvatar = (username: string): string | undefined => {
  if (!username) return undefined;
  const match = KNOWN_USER_AVATARS.find((entry) => entry.test(username));
  return match?.src;
};

export const resolveAvatarUsername = (username: string): string =>
  isSystemUser(username) ? SYSTEM_USER_DISPLAY_NAME : username;

export const Avatar = ({
  username,
  image,
  displayText = null,
  size = "default",
  tooltip = false,
  isLoading = false,
}: AvatarProps): JSX.Element => {
  const resolvedName = resolveAvatarUsername(username);
  const initials =
    resolvedName.length > 8 ? resolvedName.substring(0, 1) : resolvedName;
  const resolvedImage = image || getDefaultAvatar(username);
  const [imgError, setImgError] = useState(false);
  const resolvedDisplayText = displayText
    ? resolveAvatarUsername(displayText)
    : null;
  const avatarSize = avatarSizeMap[size];

  const showImage = resolvedImage && !imgError;
  return (
    <Tooltip title={tooltip && (resolvedDisplayText || resolvedName)}>
      <ElementContainer ai="center" mr={1}>
        {isLoading ? (
          <LoadingAvatar
            aria-label="Loading avatar"
            aria-busy="true"
            size={avatarSize.width}
          />
        ) : (
          <AntAvatar
            size={avatarSize.width}
            style={{
              backgroundColor: showImage
                ? undefined
                : getBackground(resolvedName),
              color: "#fff",
              fontSize: avatarSize.fontSize,
              lineHeight: avatarSize.lineHeight,
            }}
            src={showImage ? resolvedImage : undefined}
            onError={(): boolean => {
              setImgError(true);
              return false;
            }}
          >
            {formatName(initials)}
          </AntAvatar>
        )}
        {resolvedDisplayText && (
          <CompassParagraph ml={5} fontSize={12} color={greyText}>
            {resolvedDisplayText}
          </CompassParagraph>
        )}
      </ElementContainer>
    </Tooltip>
  );
};

export const AvatarStack = ({
  items,
  maxCount,
  enableExpand = true,
}: AvatarStackProps): JSX.Element => {
  const DEFAULT_MAX_COUNT = 4;
  const [expanded, setExpanded] = useState(false);

  const style = avatarSizeMap[items[0]?.size || "default"];
  const effectiveMax = expanded ? items.length : maxCount || DEFAULT_MAX_COUNT;

  const handleClick = (): void => {
    setExpanded((prev) => !prev);
  };

  return (
    <StackWrapper
      style={{ display: "flex" }}
      onClick={(e): void => {
        if (
          enableExpand &&
          (e.target as HTMLElement).closest(".ant-avatar-string")
        ) {
          handleClick();
        }
      }}
    >
      <AntAvatar.Group
        max={{
          popover: { onOpenChange: () => <></> },
          count: effectiveMax,
          style: {
            ...style,
            color: lightBlueText,
            backgroundColor: lightBlueBackground,
            border: `1px solid ${lightBlueBackground}`,
            cursor: "pointer",
          },
        }}
      >
        {items?.map(
          (
            {
              username,
              image,
              displayText = null,
              size = "default",
              tooltip = false,
              borderColor = null,
              bgColor = null,
            },
            idx,
          ) => {
            const initials =
              username?.length > 8 ? username.substring(0, 1) : username;
            const title = tooltip ? displayText || username : undefined;

            const node = (
              <AntAvatar
                key={`${username}-${idx}`}
                size={avatarSizeMap[size].width}
                style={{
                  backgroundColor: image
                    ? undefined
                    : bgColor || getBackground(username),
                  color: "#fff",
                  border: borderColor ? `1px solid ${borderColor}` : undefined,
                  cursor: "pointer",
                  fontSize: avatarSizeMap[size].fontSize,
                  lineHeight: avatarSizeMap[size].lineHeight,
                }}
                src={image || undefined}
                onClick={handleClick}
              >
                {!image && formatName(initials)}
              </AntAvatar>
            );

            return title ? (
              <Tooltip title={title} key={`tip-${username}-${idx}`}>
                {node}
              </Tooltip>
            ) : (
              node
            );
          },
        )}
      </AntAvatar.Group>
    </StackWrapper>
  );
};

export const getAvatarOption = ({ name }: { name: string }): JSX.Element => (
  <Avatar username={name[0]} size="small" displayText={name} />
);

export const AvatarMatrix = ({
  stacks,
  maxCount,
  enableExpand = true,
}: AvatarMatrixProps): JSX.Element => {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {stacks.map((items, idx) => (
        <ElementContainer key={idx} mt={idx === 0 ? 1 : -4}>
          <AvatarStack
            key={idx}
            items={items}
            maxCount={maxCount}
            enableExpand={enableExpand}
          />
        </ElementContainer>
      ))}
    </div>
  );
};

export type AvatarProps = {
  username: string;
  image?: string | null;
  displayText?: string | null;
  size?: AntAvatarSize;
  tooltip?: boolean;
  isLoading?: boolean;
  borderColor?: string | null;
  cursor?: "default" | "pointer";
  bgColor?: string | null;
};

export type AvatarStackProps = {
  items: AvatarProps[];
  maxCount?: number;
  enableExpand?: boolean;
};

export type AvatarMatrixProps = {
  stacks: AvatarStackProps["items"][];
  maxCount?: number;
  enableExpand?: boolean;
};
